import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DisplayCard } from '..'
import SortingType from '../../constants/SortingType'
import getChargerImage from '../../mixins/getChargerImage'
import { IChargerItem } from '../../typeScript/interfaces/IChargersPageResponse'
import { DisplayCardTypes } from '../DisplayCard'
import ChargerAuthIcon from './ChargerAuthIcon'

import desc from '../../assets/GridSortUp.svg'

import asc from '../../assets/GridSortDown.svg'

type Props = {
  chargers: IChargerItem[]
  handleRowClick: (e: any) => void
}

const Grid = (props: Props) => {
  const { chargers, handleRowClick } = props
  const { t } = useTranslation()
  const [sortDirection, setSortDirection] = useState<SortingType>(
    SortingType.DESC
  )
  const [sortField, setSortField] = useState<string>('chargerName')

  useEffect(() => {
    sortChargers(sortField, true)
  }, [chargers])

  const getNestedValue = (obj: IChargerItem, path: string): string => {
    const value = path.split('.').reduce((acc: unknown, part: string) => {
      if (acc && typeof acc === 'object') {
        return (acc as Record<string, unknown>)[part]
      }
      return undefined
    }, obj)
    return typeof value === 'string' ? value : String(value ?? '')
  }

  const sortChargers = (fieldName: string, switchDirection = false) => {
    let direction = sortDirection
    if (!switchDirection) {
      if (sortField === fieldName)
        direction =
          sortDirection === SortingType.ASC ? SortingType.DESC : SortingType.ASC
      else direction = SortingType.DESC
    }
    chargers.sort(function (a, b) {
      const textA = getNestedValue(a, fieldName)
      const textB = getNestedValue(b, fieldName)
      return direction === SortingType.DESC
        ? textA.localeCompare(textB)
        : textB.localeCompare(textA)
    })
    setSortDirection(direction)
    setSortField(fieldName)
  }

  const displayFilterImage = (sortedField, fieldName, order) => {
    if (sortedField !== fieldName) return null
    else {
      if (order === SortingType.ASC)
        return <img src={asc} alt="" className="w-5" />
      else if (order === SortingType.DESC)
        return <img src={desc} alt="" className="w-5" />
      else return null
    }
  }

  const gridColumnHeader = (sortDirection, sortedField, sortColumn) => {
    return (
      <DisplayCard type={DisplayCardTypes.TRANSPARENT_REDUCED} className="my-6">
        <div className="grid grid-cols-5 lg:grid-cols-12 gap-4">
          <div className="col-span-3 flex flex-wrap items-center">
            {gridColumnHeaderItem(
              sortDirection,
              t('charger:charger_name'),
              sortedField,
              'chargerName',
              sortColumn
            )}
          </div>
          <div className="col-span-3 hidden lg:flex flex-wrap items-center text-sm">
            {gridColumnHeaderItem(
              sortDirection,
              t('charger:charger_address'),
              sortedField,
              'site.address.address1',
              sortColumn
            )}
          </div>
          <div className="col-span-2 hidden lg:flex flex-wrap items-center text-sm">
            {gridColumnHeaderItem(
              sortDirection,
              t('charger:charger_zipCode'),
              sortedField,
              'site.address.zipCode',
              sortColumn
            )}
          </div>
          <div className="col-span-2 hidden lg:flex flex-wrap items-center text-sm">
            {gridColumnHeaderItem(
              sortDirection,
              t('charger:charger_city'),
              sortedField,
              'site.address.city',
              sortColumn
            )}
          </div>
          <div className="col-span-2 flex flex-wrap items-center text-sm">
            {gridColumnHeaderItem(
              sortDirection,
              t('charger:charger_model'),
              sortedField,
              'modelName',
              sortColumn
            )}
          </div>
        </div>
      </DisplayCard>
    )
  }

  const gridColumnHeaderItem = (
    order,
    headerText,
    sortedField,
    fieldName,
    sortClick
  ) => {
    return (
      <div
        className={`truncate cursor-pointer flex items-center ${
          sortedField === fieldName ? 'font-bold' : ''
        }`}
      >
        <span
          className="truncate"
          onClick={() => sortClick(fieldName)}
          title={headerText}
        >
          {headerText}
        </span>
        {displayFilterImage(sortedField, fieldName, order)}
      </div>
    )
  }

  return (
    <div className="charger-list__grid">
      <hr />
      {gridColumnHeader(sortDirection, sortField, sortChargers)}
      {chargers.map((charger, index) => {
        return (
          <DisplayCard
            type={DisplayCardTypes.PLAIN_REDUCED}
            className="mb-3"
            onClick={() => handleRowClick(charger.id)}
            key={index}
          >
            <div className="grid grid-cols-5 lg:grid-cols-12 gap-4">
              <div className="col-span-3 flex flex-wrap">
                {/* TODO::: DYNAMIC CHARGER IMAGE */}
                <div className="whitespace-nowrap flex items-center">
                  <img
                    src={getChargerImage(charger.modelName)}
                    className="w-12"
                    alt=""
                  />
                  <span className="truncate" title={charger.chargerName}>
                    {charger.chargerName}
                  </span>
                </div>
              </div>
              <div className="col-span-3 hidden lg:flex flex-wrap items-center text-sm">
                <span title={charger.site.address.address1}>
                  {charger.site.address.address1}
                </span>
              </div>
              <div className="col-span-2 hidden lg:flex flex-wrap items-center text-sm">
                <span title={charger.site.address.zipCode}>
                  {charger.site.address.zipCode}
                </span>
              </div>
              <div className="col-span-2 hidden lg:flex flex-wrap items-center text-sm">
                <span title={charger.site.address.city}>
                  {charger.site.address.city}
                </span>
              </div>
              <div className="col-span-2 flex flex-wrap items-center text-sm">
                <span title={charger.modelName}>{charger.modelName}</span>
              </div>
            </div>
          </DisplayCard>
        )
      })}
    </div>
  )
}

export default Grid
